// frontend/src/utils/autosave.js
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import axios from "axios";

const hashContent = (deltaOrHtml) => {
  try {
    const s = typeof deltaOrHtml === "string"
      ? deltaOrHtml
      : JSON.stringify(deltaOrHtml?.ops || deltaOrHtml || {});
    const n = s.length;
    return `${n}:${s.slice(0,200)}::${s.slice(-200)}`;
  } catch {
    return Math.random().toString(36).slice(2);
  }
};

const getEditorPayload = (quill) => {
  if (!quill) return { delta: null, html: "" };
  return { delta: quill.getContents(), html: quill.root?.innerHTML || "" };
};

export default function useAutoSave({
  quillRef,
  postId,                       // currentArticleId
  token,
  debounceMs = 1200,            // 5000,
  throttleMs = 5000,            // 10000,
  onLoadedHead,                 // optional: (content) => void (to hydrate editor if you want)
  onSavedBadge,                 // optional: (text) => void to display "Saved …" in parent
}) {
  const [isSaving, setIsSaving] = useState(false);
  const [lastCommitAt, setLastCommitAt] = useState(null);
  const [headVersionId, setHeadVersionId] = useState(null);
  const [lastManual, setLastManual] = useState(null);     // { id, ts } | null
  const [autosaves, setAutosaves] = useState([]);         // [{ id, ts }]
  const [etag, setEtag] = useState(null);

  const autosaveTimerRef = useRef(null);
  const throttleUntilRef = useRef(0);
  const lastHashRef = useRef(null);

  const auth = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  const commitVersion = useCallback(async (type = "auto") => {
    if (!postId) return;
    const now = Date.now();
    if (type === "auto" && now < throttleUntilRef.current) return;

    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;

    const { delta, html } = getEditorPayload(quill);
    const payload = (delta && delta.ops?.length) ? delta : html;
    const h = hashContent(payload);
    if (h === lastHashRef.current) return; // no-op

    try {
      setIsSaving(true);
      const { data } = await axios.post(
        `/posts/${postId}/commit`,
        { type, content: payload, parentId: headVersionId || null, etag: etag || undefined },
        { headers: auth }
      );

      setHeadVersionId(data.headVersionId || null);
      setEtag(data.etag || null);
      if (type === "manual" && data.lastManualVersionId) {
        setLastManual({ id: data.lastManualVersionId, ts: new Date().toISOString() });
      }
      if (Array.isArray(data.autosaves)) setAutosaves(data.autosaves);

      lastHashRef.current = h;
      setLastCommitAt(new Date());
      onSavedBadge?.("Saved • just now");
      setTimeout(() => onSavedBadge?.(""), 5000);

      if (type === "auto") {
        throttleUntilRef.current = Date.now() + throttleMs;
      }
    } catch (e) {
      // 409 => etag conflict
      const msg = e?.response?.data?.error || e?.response?.data?.message || "Failed to save changes.";
      // Let the caller toast; keep hook UI-agnostic
      console.error("commitVersion failed:", msg);
    } finally {
      setIsSaving(false);
    }
  }, [postId, quillRef, headVersionId, etag, auth, onSavedBadge, throttleMs]);

  const commitRef = useRef(commitVersion);

  useEffect(() => { commitRef.current = commitVersion; }, [commitVersion]);

  const restoreVersion = useCallback(async (versionId) => {
    if (!postId || !versionId) return;
    try {
      const { data } = await axios.get(`/posts/${postId}/versions/${versionId}`, { headers: auth });
      const quill = quillRef.current?.getEditor?.();
      if (!quill) return;

      const c = data?.content;
      if (c && typeof c === "object" && Array.isArray(c.ops)) {
        quill.setContents(c, "silent");
      } else if (typeof c === "string") {
        quill.clipboard.dangerouslyPasteHTML(c);
      }
      await commitVersion("auto"); // soft-restore checkpoint
    } catch (e) {
      console.error("restoreVersion failed", e?.response?.data || e);
    }
  }, [postId, quillRef, auth, commitVersion]);

  // Load editor meta once (or when postId/token changes)
  useEffect(() => {
    let cancelled = false;
    if (!postId || !token) return;

    (async () => {
      try {
        const { data } = await axios.get(`/posts/${postId}/editor`, { headers: auth });
        if (cancelled) return;

        setHeadVersionId(data?.head?.id || null);
        setLastManual(data?.lastManual || null);
        setAutosaves(data?.autosaves || []);
        setEtag(data?.etag || data?.updatedAt || null);

        // Initialize lastHash to current content so we don’t immediately autosave
        const q = quillRef.current?.getEditor?.();
        if (data?.head?.content && onLoadedHead) onLoadedHead(data.head.content);
        if (q) {
          const { delta, html } = getEditorPayload(q);
          lastHashRef.current = hashContent(delta || html);
        }
      } catch (e) {
        console.error("Failed to load editor meta", e?.response?.data || e);
      }
    })();

    return () => { cancelled = true; };
  }, [postId, token]); // auth uses token internally; safe

  useEffect(() => {
    if (!postId) return;

    let q = null;
    let pollId = null;

    const onTextChange = (_delta, _old, source) => {
      if (source !== "user") return;

      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
        // console.log("[autosave] cleared pending timer");
      }

      autosaveTimerRef.current = setTimeout(() => {
        console.log("[autosave] firing autosave");
        commitRef.current("auto");
      }, debounceMs);

      // console.log("[autosave] timer set for", debounceMs, "ms");
    };

    const attach = () => {
      q = quillRef.current?.getEditor?.();
      if (!q) return false;
      q.on("text-change", onTextChange);
      return true;
    };

    // attach now or poll until Quill is ready
    if (!attach()) {
      pollId = setInterval(() => {
        if (attach()) {
          clearInterval(pollId);
          pollId = null;
        }
      }, 200);
    }

    return () => {
      if (pollId) clearInterval(pollId);
      if (q) q.off("text-change", onTextChange);
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, [postId, quillRef, debounceMs]); // note: no commitVersion here

  return {
    // state for UI
    isSaving,
    lastCommitAt,
    headVersionId,
    lastManual,
    autosaves,
    etag,
    // actions
    commitManual: () => commitVersion("manual"),
    commitAutoNow: () => commitVersion("auto"),
    restoreVersion,
  };
}
